// must support both server and client components (because Button is used in both server and client components)
import React, { ComponentProps, ReactNode, ButtonHTMLAttributes } from "react";
import { MouseEventHandler } from "react";
import styles from "./Button.module.scss";
import { customSanitize } from "@/utils/customSanitize.ts";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { CustomLink } from "../CustomLink";
import { FadeInFromBottom, FadeInWithPulse } from "../Animation/Animation";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";
import ButtonClient from "./Button.client";

export type ButtonTypes = "primary" | "secondary";

interface ButtonProps extends ComponentProps<"a"> {
  type: ButtonTypes;
  children: ReactNode;
  animation?: "fade" | "pulse" | "none";
  /** Renders normal button component with on Click Event Handler*/
  onClick?: MouseEventHandler<HTMLElement>;
  href: string;
  className?: string;
  target: string;
  /**
   * Creates a link with no prefetching abilities... which Next Links per default have
   */
  disablePrefetch?: boolean;
  /**
   * Creates a link which triggers prefetching only on hover... Next Links prefetch per default as soon as link is visible
   */
  onlyPrefetchOnHover?: boolean;
  isDownload?: boolean;
  isBack?: boolean;
  disabled?: boolean;
}

export const Button = ({
  type,
  children,
  animation,
  onClick,
  href = "_self",
  className,
  target = "_self",
  disablePrefetch = false,
  onlyPrefetchOnHover = false,
  isDownload = false,
  isBack = false,
  ...props
}: ButtonProps) => {
  const btnClassName = removeExtraSpacesFromClassNames(
    `btn ${styles.btn} ${styles[`is-` + type]} ${className || ""} ${isBack ? styles["is-back"] : ""}`,
  );

  if (!href && !onClick) {
    throw new Error(`No href provided at "Button.tsx"`);
  }
  // else if (!target && !onClick) {
  //   throw new Error(`No target provided at "Button.tsx" at ${asPath}`);
  // }

  const innerButtonJSX =
    !!href && !onClick ? (
      <CustomLink
        href={href}
        target={target}
        className={btnClassName}
        disablePrefetch={disablePrefetch}
        onlyPrefetchOnHover={onlyPrefetchOnHover}
        {...props}
      >
        {children}
        {type === "secondary" && (
          <CustomSVG
            iconKey={"altArrowRight"}
            className="is-flex"
            wrapper={"span"}
            width={24}
            height={24}
            aria-hidden={true}
          />
        )}
        {type === "primary" && isDownload && (
          <CustomSVG
            iconKey={"download"}
            className="is-flex"
            wrapper={"span"}
            width={16}
            height={16}
            aria-hidden={true}
          />
        )}
      </CustomLink>
    ) : (
      onClick && (
        <ButtonClient
          btnClassName={btnClassName}
          isDownload={isDownload}
          type={type}
          props={props as ComponentProps<"button">}
          onClick={onClick}
        >
          {children}
        </ButtonClient>
      )
    );

  if (!innerButtonJSX) {
    return null;
  }

  return animation === "fade" ? (
    <FadeInFromBottom>{innerButtonJSX}</FadeInFromBottom>
  ) : animation === "pulse" ? (
    <FadeInWithPulse>{innerButtonJSX}</FadeInWithPulse>
  ) : (
    innerButtonJSX
  );
};

Button.displayName = "Button";

interface SimpleButtonProp extends ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  extraClassName?: string;
  btnType?: ButtonTypes;
}

export const SimpleButton = ({
  text,
  extraClassName,
  btnType = "primary",
  ...props
}: SimpleButtonProp) => {
  const btnClassName = removeExtraSpacesFromClassNames(
    `button-reset btn ${styles.btn} ${styles[`is-${btnType}`]} ${extraClassName || ""}`,
  );

  return (
    <button className={btnClassName} {...props}>
      <span dangerouslySetInnerHTML={{ __html: customSanitize(text) }}></span>
      {btnType === "secondary" && (
        <CustomSVG
          iconKey={"altArrowRight"}
          className="is-flex"
          wrapper={"span"}
          width={24}
          height={24}
          aria-hidden={true}
        />
      )}
    </button>
  );
};

SimpleButton.displayName = "SimpleButton";
